import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Users, CalendarClock, CheckCircle2, Send } from "lucide-react";
import { AddLeadDialog } from "@/components/recruiting/AddLeadDialog";
import { toast } from "sonner";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null);

const STATUS_TONE = {
  "Prescreen Scheduled": "bg-sky-100 text-sky-700",
  "Prescreen Complete": "bg-indigo-100 text-indigo-700",
  "Submitted for Internal Review": "bg-emerald-100 text-emerald-700",
  Candidate: "bg-violet-100 text-violet-700", Lead: "bg-slate-100 text-slate-600",
};

function SchedulePrescreenDialog({ row, onClose, onDone }) {
  const [dt, setDt] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!dt) { toast.error("Pick a date & time."); return; }
    setBusy(true);
    try {
      await api.post("/interviews/prescreen/schedule", { person_id: row.person_id, scheduled_at: new Date(dt).toISOString(), notes });
      toast.success("Pre-Screen scheduled"); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="schedule-prescreen-dialog">
        <DialogHeader><DialogTitle>Schedule Pre-Screen — {row.person_name}</DialogTitle>
          <DialogDescription className="sr-only">Schedule a pre-screen call with this candidate.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Date & Time</Label><Input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} data-testid="schedule-prescreen-datetime" /></div>
          <div><Label>Notes (optional)</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="schedule-prescreen-notes" /></div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-strong text-white" disabled={busy} onClick={submit} data-testid="schedule-prescreen-submit">{busy ? "Scheduling…" : "Schedule Pre-Screen"}</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PrescreenDialog({ row, onClose, onDone }) {
  const [form, setForm] = useState(null);
  const [ans, setAns] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/interviews/prescreen-form").then((r) => setForm(r.data)).catch((e) => { toast.error(apiError(e)); onClose(); }); }, []);
  const set = (k, v) => setAns((a) => ({ ...a, [k]: v }));
  const visible = (q) => !q.show_if || Object.entries(q.show_if).every(([k, v]) => ans[k] === v);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/interviews/prescreen/complete", { person_id: row.person_id, answers: ans });
      toast.success("Pre-Screen completed"); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="prescreen-dialog">
        <DialogHeader><DialogTitle>Complete Pre-Screen — {row.person_name}</DialogTitle>
          <DialogDescription className="sr-only">Complete the standardized pre-screening interview.</DialogDescription></DialogHeader>
        {!form ? <p className="text-sm text-slate-400 py-6 text-center">Loading form…</p> : (
          <div className="space-y-5">
            {form.sections.map((s, si) => (
              <div key={si} className="space-y-3">
                <h4 className="text-sm font-display font-semibold text-slate-900 border-b border-slate-100 pb-1">{s.title}</h4>
                {s.questions.filter(visible).map((q) => (
                  <div key={q.key} data-testid={`prescreen-q-${q.key}`}>
                    <Label className="text-xs">{q.label}{q.required && <span className="text-rose-500"> *</span>}</Label>
                    {q.type === "long" ? (
                      <Textarea rows={2} value={ans[q.key] || ""} onChange={(e) => set(q.key, e.target.value)} />
                    ) : q.type === "yesno" ? (
                      <Select value={ans[q.key] || ""} onValueChange={(v) => set(q.key, v)}><SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                        <SelectContent className="bg-white"><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent></Select>
                    ) : q.type === "select" ? (
                      <Select value={ans[q.key] || ""} onValueChange={(v) => set(q.key, v)}><SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                        <SelectContent className="bg-white">{q.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
                    ) : q.type === "multi" ? (
                      <div className="flex flex-wrap gap-1.5 mt-1">{q.options.map((o) => {
                        const arr = ans[q.key] || []; const on = arr.includes(o);
                        return <button type="button" key={o} onClick={() => set(q.key, on ? arr.filter((x) => x !== o) : [...arr, o])}
                          className={`px-2.5 py-1 rounded-full text-xs ${on ? "bg-brand text-white" : "bg-slate-100 text-slate-600"}`}>{o}</button>;
                      })}</div>
                    ) : (
                      <Input value={ans[q.key] || ""} onChange={(e) => set(q.key, e.target.value)} />
                    )}
                  </div>
                ))}
              </div>
            ))}
            <div className="flex justify-end gap-2 sticky bottom-0 bg-white pt-2"><Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-brand hover:bg-brand-strong text-white" disabled={busy} onClick={submit} data-testid="prescreen-submit">{busy ? "Saving…" : "Complete Pre-Screen"}</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MyReferrals() {
  const { isOutside } = useAuth();
  const [items, setItems] = useState(null);
  const [leadOpen, setLeadOpen] = useState(false);
  const [sched, setSched] = useState(null);
  const [prescreen, setPrescreen] = useState(null);

  const load = () => api.get("/interviews/my-referrals").then((r) => setItems(r.data.items || [])).catch((e) => { toast.error(apiError(e)); setItems([]); });
  useEffect(() => { if (isOutside) load(); }, [isOutside]);
  if (!isOutside) return <Navigate to="/recruiting" replace />;

  const submitReview = async (row) => {
    try { await api.post("/interviews/prescreen/submit-review", { person_id: row.person_id }); toast.success("Submitted for Internal Review"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const rowAction = (row) => {
    const ps = row.prescreen_status;
    if (ps === "submitted_for_review") return <span className="text-xs text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Handed to internal team</span>;
    if (ps === "completed") return <Button size="sm" className="bg-brand hover:bg-brand-strong text-white" onClick={() => submitReview(row)} data-testid={`action-submit-review-${row.person_id}`}><Send className="w-3.5 h-3.5 mr-1.5" />Submit for Internal Review</Button>;
    if (ps === "scheduled") return <Button size="sm" variant="outline" onClick={() => setPrescreen(row)} data-testid={`action-complete-prescreen-${row.person_id}`}>Complete Pre-Screen</Button>;
    return <Button size="sm" variant="outline" onClick={() => setSched(row)} data-testid={`action-schedule-prescreen-${row.person_id}`}><CalendarClock className="w-3.5 h-3.5 mr-1.5" />Schedule Pre-Screen</Button>;
  };

  return (
    <div data-testid="my-candidates-page">
      <PageHeader title="My Leads & Candidates" description="Leads you've added, candidates you've referred, and candidates assigned to you. Schedule and complete a Pre-Screen, then submit for internal review."
        actions={<Button className="bg-brand hover:bg-brand-strong text-white" onClick={() => setLeadOpen(true)} data-testid="my-candidates-add-lead-btn"><UserPlus className="w-4 h-4 mr-1.5" />Add Lead</Button>} />
      <AddLeadDialog open={leadOpen} onClose={() => setLeadOpen(false)} onCreated={() => { setLeadOpen(false); load(); }} />
      {sched && <SchedulePrescreenDialog row={sched} onClose={() => setSched(null)} onDone={() => { setSched(null); load(); }} />}
      {prescreen && <PrescreenDialog row={prescreen} onClose={() => setPrescreen(null)} onDone={() => { setPrescreen(null); load(); }} />}

      {items === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon={Users} title="No leads yet" description="Leads you add appear here. Then schedule a Pre-Screen, complete it, and submit for internal review."
          action={<Button variant="outline" onClick={() => setLeadOpen(true)} data-testid="my-candidates-empty-cta"><UserPlus className="w-4 h-4 mr-1.5" />Add Lead</Button>} testId="my-candidates-empty" />
      ) : (
        <>
          <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-3">Candidate</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Referred</th>
                  <th className="px-4 py-3">Pre-Screen</th><th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, i) => (
                  <tr key={i} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60" data-testid={`my-candidate-row-${i}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">{r.person_name}</td>
                    <td className="px-4 py-3"><span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_TONE[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status}</span></td>
                    <td className="px-4 py-3 text-slate-500">{fmtDate(r.date_referred)}</td>
                    <td className="px-4 py-3 text-slate-600">{r.prescreen_scheduled_at ? <span className="inline-flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5 text-brand" />{fmtWhen(r.prescreen_scheduled_at)}</span> : <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3 text-right">{rowAction(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden space-y-2">
            {items.map((r, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid={`my-candidate-card-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-800">{r.person_name}</span>
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_TONE[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1 mb-2">Referred {fmtDate(r.date_referred)}{r.prescreen_scheduled_at ? ` · Pre-Screen ${fmtWhen(r.prescreen_scheduled_at)}` : ""}</p>
                <div>{rowAction(r)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
